import { useState, useMemo, useEffect } from 'react'
import { isAddress } from 'viem'
import { useAccount } from 'wagmi'
import { useSwitchChain } from '~/hooks'
import EmptySearchResult from './EmptySearchResult'
import { TradeDirection } from '~/components/Exchange/types'
import { useDirectionChange } from '~/components/LimitOrder/hooks/useDirectionChange'
import {
  OrderedLimitSwapToken,
  useOrderedLimitSwapTokens,
  useOrderedLimitSwapTokensByRecent,
} from '~/components/LimitOrder/hooks/useOrderedTokenList'
import { useAllSortedLimitTokens } from '~/components/LimitOrder/hooks/useAllSortedLimitTokens'
import { useToken } from '~/hooks/useToken'
import { useAppDispatch } from '~/state/hooks'
import {
  StyledTokenList,
  StyledTabWrapper,
  StyledTabLabel,
  StyledSwitcherWrapper,
  StyledIconSwitcher,
  StyledTokenListContainer,
} from '../styled'
import { TokenSearchInput } from './TokenSearchInput'
import { useTranslation } from 'react-i18next'
import TokenSelectWarning from '~/components/Exchange/components/TokenSelectWarning'
import NetworkList from './NetworkList'
import TokenRecommendations from './TokenRecommendations'
import TokenListItem from './TokenListItem'
import AddCustomTokenModal from '../../AddCustomTokenModal'
import { useLimitSwapPair } from '~/state/dex/hooks'
import {
  addCustomLimitTokens,
  updateLimitSwapPairToken,
} from '~/state/dex/actions'
import { updatePendingChainId } from '~/state/user/actions'
import { useGA, useUpdaterChainId } from '~/hooks'
import { message } from '~/components/Message'
import { getChainById } from '~/utils/chain'
import { compareAddress } from '~/utils/token'
import { delay } from '~/utils/common'
import { useClearQueryParams } from '~/components/LimitOrder/hooks'

export const TokenList = ({ direction, onDismiss }) => {
  const { t } = useTranslation()

  const { chainId, address: account } = useAccount()
  const isConnected = !!account
  const { switchChain } = useSwitchChain()
  const expectChainId = useUpdaterChainId()
  const defaultSelectedChainId = useMemo(
    () => (account ? expectChainId : null),
    [account, expectChainId],
  )
  const [selectedChainId, setSelectedChainId] = useState<number | null>(
    defaultSelectedChainId,
  )

  const orderedLimitSwapTokens = useOrderedLimitSwapTokens(selectedChainId)
  const orderedLimitSwapTokensByRecent = useOrderedLimitSwapTokensByRecent(
    selectedChainId,
  )
  const allTokens = useAllSortedLimitTokens()

  const [inputToken, outputToken] = useLimitSwapPair()
  const changeDirection = useDirectionChange()
  const [searchText, setSearchText] = useState('')
  const [sortByValue, setSortByValue] = useState(true)

  const dispatch = useAppDispatch()
  const { trackClick } = useGA()
  const customTokenAddress = useMemo(
    () => (isAddress(searchText, { strict: false }) ? searchText : ''),
    [searchText],
  )
  const customToken = useToken(customTokenAddress) as LimitSwapToken
  const [tokenWarningVisible, setTokenWarningVisible] = useState(false)
  const [warningToken, setWarningToken] = useState<LimitSwapToken | undefined>()

  const isSelectedAll = selectedChainId === null

  const displayTokens = useMemo(() => {
    if (
      (!orderedLimitSwapTokens || !orderedLimitSwapTokens.length) &&
      (!allTokens || !allTokens.length)
    ) {
      return []
    }

    const targetTokens: OrderedLimitSwapToken[] = isSelectedAll
      ? allTokens
      : sortByValue
      ? orderedLimitSwapTokens
      : orderedLimitSwapTokensByRecent

    // First display symbol matches
    // Then display symbol starts with search text
    // Then display symbol contains search text
    // Then display name contains search text
    // Finally display address matches
    if (searchText) {
      const searchLowerCase = searchText.toLowerCase()
      return targetTokens
        .filter((token) => {
          const { symbol, address, name } = token
          return (
            symbol.toLowerCase().includes(searchLowerCase) ||
            name?.toLowerCase().includes(searchLowerCase) ||
            address.toLowerCase() === searchLowerCase
          )
        })
        .map((token) => {
          const symbolLower = token.symbol.toLowerCase()
          const nameLower = token.name?.toLowerCase() || ''
          let score = 0

          if (searchLowerCase === 'eth' && symbolLower === 'weth') {
            score = 200
          } else if (symbolLower.includes(searchLowerCase)) {
            score = 100
            if (symbolLower === searchLowerCase) {
              score = 150
            } else if (symbolLower.startsWith(searchLowerCase)) {
              score = 120
            }
          } else if (nameLower.includes(searchLowerCase)) {
            score = 50
          } else if (token.address.toLowerCase() === searchLowerCase) {
            score = 10
          }
          return { ...token, score }
        })
        .sort((a, b) => b.score - a.score)
    }
    return targetTokens.filter((token) => !token.blocked)
  }, [
    searchText,
    orderedLimitSwapTokens,
    orderedLimitSwapTokensByRecent,
    allTokens,
    selectedChainId,
  ])

  const clearQueryParams = useClearQueryParams()

  const onSelect = async (token: OrderedLimitSwapToken) => {
    trackClick(
      `webtl_lo_${
        direction === TradeDirection.Input ? 'taker' : 'maker'
      }_select`,
      { symbol: token.symbol, address: token.address },
    )
    const selectedChainId = token.chainId
    const isNetworkChanged = selectedChainId !== expectChainId

    clearQueryParams()

    if (isNetworkChanged) {
      if (isConnected) {
        await switchChain({ chainId: selectedChainId })
        await delay(300)
      }
      dispatch(updatePendingChainId(selectedChainId))
    }

    if (token.blocked) {
      setWarningToken(token)
      setTokenWarningVisible(true)
      return
    }

    const isChangeDirection =
      !isNetworkChanged &&
      ((direction === TradeDirection.Input &&
        compareAddress(token.address, outputToken?.address)) ||
        (direction === TradeDirection.Output &&
          compareAddress(token.address, inputToken?.address)))
    if (isChangeDirection) {
      changeDirection()
    } else {
      dispatch(
        updateLimitSwapPairToken({
          direction,
          token: {
            chainId: token.chainId,
            symbol: token.symbol,
            address: token.address,
            name: token.name,
            decimals: token.decimals,
            logo: token.logo,
            displayType: token.displayType,
            recommend: token.recommend,
          },
        }),
      )
    }
    onDismiss()
  }

  const onSearch = (s: string) => {
    setSearchText(s)
  }

  const onSelectCustomToken = () => {
    if (chainId !== expectChainId) {
      const expectChain = getChainById(expectChainId)
      const chain = getChainById(chainId)
      message.error(
        t('unmatched_network_message', {
          chain: `${chain.chainName} ChainID: ${chainId}`,
          expectChain: expectChain.chainName,
        }),
      )
      return
    }
    setWarningToken(customToken)
    setTokenWarningVisible(true)
  }

  const onWarningClose = () => {
    setTokenWarningVisible(false)
  }
  const onWarningSelect = () => {
    if (
      warningToken.address.toLowerCase() === customToken.address.toLowerCase()
    ) {
      dispatch(
        addCustomLimitTokens({
          chainId: expectChainId,
          tokens: [
            {
              ...customToken,
              chainId: expectChainId,
              displayType: 999,
              recommend: false,
            },
          ],
        }),
      )
      dispatch(updateLimitSwapPairToken({ direction, token: customToken }))
    }
    onWarningClose()
    onSelect({
      chainId,
      symbol: customToken.symbol,
      address: customToken.address,
      name: customToken.name,
      decimals: customToken.decimals,
      logo: customToken.logo,
      displayType: 999,
      recommend: false,
    })
  }

  const handleSwitch = () => {
    setSortByValue(!sortByValue)
  }

  useEffect(() => {
    if (!isConnected || selectedChainId !== expectChainId) {
      setSortByValue(true)
    }
  }, [isConnected, selectedChainId])

  const isEmptyResult = !displayTokens.length && !customToken

  return (
    <StyledTokenListContainer>
      <TokenSearchInput onSearch={onSearch} />
      <NetworkList
        selectedChainId={selectedChainId}
        setSelectedChainId={setSelectedChainId}
      />
      {searchText ? null : (
        <TokenRecommendations
          selectedChainId={selectedChainId}
          onTokenSelected={onSelect}
        />
      )}
      {!isEmptyResult && (
        <StyledTabWrapper>
          <StyledTabLabel>{t('all')}</StyledTabLabel>
          {isConnected && selectedChainId === expectChainId && (
            <StyledSwitcherWrapper onClick={handleSwitch}>
              {sortByValue ? t('token_value') : t('recent_trades')}
              <StyledIconSwitcher />
            </StyledSwitcherWrapper>
          )}
        </StyledTabWrapper>
      )}
      <StyledTokenList>
        {isEmptyResult ? (
          <EmptySearchResult isSearchByAddress={!!customTokenAddress} />
        ) : null}
        {customToken && !displayTokens.length ? (
          <TokenListItem
            showContractAddr
            custom
            token={customToken}
            onSelect={onSelectCustomToken}
            showNetworkBadge={isSelectedAll}
          />
        ) : null}
        {displayTokens.map((token) => (
          <TokenListItem
            showContractAddr={Boolean(searchText)}
            token={token}
            key={token.address}
            onSelect={onSelect}
            showNetworkBadge={isSelectedAll}
          />
        ))}
      </StyledTokenList>
      <AddCustomTokenModal direction={direction} onDismiss={onDismiss} />
      <TokenSelectWarning
        visible={tokenWarningVisible}
        token={warningToken}
        blocked={warningToken?.blocked}
        onClose={onWarningClose}
        onSelect={onWarningSelect}
      />
    </StyledTokenListContainer>
  )
}
